import { useDisclosure } from "@mantine/hooks";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { useUpdateProductLine } from "#modules/productLines/view/useUpdateProductLine";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useUpdatePredictionData } from "#modules/quoteRequestLines/useUpdatePredictionData";
import { useStrategy } from "#modules/quoteRequestLines/view/useStrategy";
import { ROUTES } from "#shared/routes";
import {
  getFirst2DFile,
  JobStatusSchema,
  JobStatusType,
  PartTypeSchema,
  isJobProcessing,
} from "shared";

type UseQuoteRequestLineContentProps = {
  quoteRequestLine: QuoteRequestLineDto;
  part: PartDto | undefined | null;
  isChild: boolean;
  hasChildren: boolean;
  polledPredictionStatus?: JobStatusType | null;
};

type UseQuoteRequestLineContentReturn = {
  isConsumable: boolean;
  shouldDisplayClusteringZone: boolean;
  isPartBeingAnalyzed: boolean;
  updateMaterial: ReturnType<typeof useUpdatePredictionData>["updateMaterial"];
  updateQuantity: ReturnType<typeof useUpdatePredictionData>["updateQuantity"];
  isPredicting: boolean;
  predictProcessSteps: ReturnType<typeof useUpdatePredictionData>["predictProcessSteps"];
  isPredictionError: boolean;
  editCluster: () => void;
  updateProductLine: ReturnType<typeof useUpdateProductLine>["updateProductLine"];
  quantityInAssembly: number;
  overlayOpened: boolean;
  closeOverlay: () => void;
  overlayDefaultTab: string | null;
  openThumbnailOnAnnotated: () => void;
  dimensionChipOpened: boolean;
  closeDimensionChip: () => void;
};

export const useQuoteRequestLineContent = ({
  quoteRequestLine,
  part,
  isChild,
  hasChildren,
  polledPredictionStatus,
}: UseQuoteRequestLineContentProps): UseQuoteRequestLineContentReturn => {
  const navigate = useNavigate();

  const quantityInAssembly = part?.quantityInAssembly;

  const { isClustering } = useStrategy();

  const [overlayOpened, { open: openOverlay, close: closeOverlay }] = useDisclosure(false);
  const [overlayDefaultTab, setOverlayDefaultTab] = useState<string | null>(null);
  const [dimensionChipOpened, { open: openDimensionChip, close: closeDimensionChip }] =
    useDisclosure(false);

  const { updateProductLine } = useUpdateProductLine({
    quoteRequestLineId: quoteRequestLine.id,
  });

  const { updateMaterial, updateQuantity, predictProcessSteps, isPredictionError } =
    useUpdatePredictionData({
      quoteRequestLine,
      isChild: Boolean(isChild),
    });

  const isConsumable = part?.type === PartTypeSchema.enum.CONSUMABLE;

  const hasCluster = !isEmpty(quoteRequestLine.cluster?.targetQuoteRequestLines);
  const shouldDisplayClusteringZone =
    hasCluster || (isClustering && (isChild || !hasChildren) && !isConsumable);

  const editCluster = () => {
    navigate(ROUTES.partnerQuoteRequestLineCompareCluster(quoteRequestLine.id));
  };

  const has2DFile = !!getFirst2DFile(part?.files ?? []);
  const isPartBeingAnalyzed =
    ((has2DFile && part?.analyze2DStatus && isJobProcessing(part.analyze2DStatus)) ||
      (part?.analyzeStatus && isJobProcessing(part.analyzeStatus))) ??
    false;

  const openThumbnailOnAnnotated = () => {
    if (has2DFile) {
      setOverlayDefaultTab(part?.urlPdfAnnotated ?? null);
      openOverlay();
    } else {
      openDimensionChip();
    }
  };
  const predictionStatus = polledPredictionStatus ?? quoteRequestLine.predictionStatus;

  return {
    isConsumable,
    shouldDisplayClusteringZone,
    isPartBeingAnalyzed,
    updateMaterial,
    updateQuantity,
    isPredicting:
      predictionStatus === JobStatusSchema.enum.STANDBY ||
      predictionStatus === JobStatusSchema.enum.PROCESSING,
    predictProcessSteps,
    isPredictionError: isPredictionError || predictionStatus === JobStatusSchema.enum.ERROR,
    editCluster,
    updateProductLine,
    quantityInAssembly: quantityInAssembly ?? 1,
    overlayOpened,
    closeOverlay,
    overlayDefaultTab,
    openThumbnailOnAnnotated,
    dimensionChipOpened,
    closeDimensionChip,
  };
};
